/* ===== bulb.js ===== */
/* sollie-pdp-v2 — bulb add-on (.sv2-bulb*)
 *
 * Dependency-free, defer-loaded, delegated listeners on document only.
 *
 * Mechanism (mirrors research/live-theme/assets/product-form.js exactly):
 *   The theme's product-form-component intercepts submit, POSTs to
 *   Theme.routes.cart_add_url with a `sections` param collected from
 *   cart-items-component[data-section-id], then dispatches a bubbling
 *   'cart:update' event (CartAddEvent) whose detail is
 *   { resource: cart, sourceId, data: { source: 'product-form-component',
 *     itemCount, productId, didError?, sections } }.
 *   cart-items-component listens for 'cart:update' and morphs itself from
 *   detail.data.sections[sectionId] (self-heals by re-fetching its section
 *   when sections are absent); cart-drawer-component[auto-open] opens on
 *   the same event.
 *
 *   We hook that exact contract: on a successful lamp add from a form whose
 *   productId matches a rendered .sv2-bulb card with its box checked, we
 *   POST /cart/add.js { items: [{ id, quantity }], sections } (the same
 *   batch shape the theme itself uses in #processBatchAddToCart), fetch
 *   /cart.js, and dispatch a matching 'cart:update' so the drawer and all
 *   listeners update the same way the theme's own add does.
 *
 * Morph safety:
 *   - The card is server-rendered Liquid, not injected DOM, so Horizon's
 *     section morphs re-render it rather than strip it.
 *   - A plain variant change morphs only <variant-picker>, but combined
 *     listings and featured-product sections morph wider scopes, and the
 *     theme's morph forces input `checked` back to server state (always
 *     unchecked). We therefore keep a tiny productId->checked store,
 *     updated by a delegated change listener, and re-apply it after
 *     'variant:update' for the SAME product only (a product switch
 *     legitimately resets the card). At add time the checkbox state is
 *     re-read from the live DOM, never from cache.
 *   - 'pageshow' re-syncs the store FROM the DOM (bfcache restores the
 *     user's checkbox state; the DOM wins there).
 *
 * Known limitation: accelerated checkout buttons (Shop Pay etc.) go
 * straight to checkout without touching the cart, so the bulb cannot be
 * attached on that path. Regular ATC and the sticky ATC (which puppets the
 * main button, see sticky-add-to-cart.js) are both covered.
 *
 * The bulb variant id is read from data-sv2-bulb-variant rendered by
 * Liquid from the live product. Nothing is hardcoded here.
 */
(function () {
  'use strict';

  if (window.__sv2BulbAddonInit) return;
  window.__sv2BulbAddonInit = true;

  var LIVE_REGION_CLEAR_MS = 4000;

  /** productId (string) -> boolean; survives section morphs. */
  var checkedByProduct = {};

  function allCards() {
    return document.querySelectorAll('[data-sv2-bulb]');
  }

  function cardInput(card) {
    return card.querySelector('[data-sv2-bulb-check]');
  }

  /* ---- 1. Track checkbox state (delegated; capture so a stopped
         propagation elsewhere can't starve us) ---------------------- */
  document.addEventListener(
    'change',
    function (event) {
      var input = event.target;
      if (!input || !input.matches || !input.matches('[data-sv2-bulb-check]')) return;
      var card = input.closest('[data-sv2-bulb]');
      if (!card) return;
      checkedByProduct[card.getAttribute('data-sv2-product-id')] = input.checked;
      /* Any interaction with the card clears a previous failure notice. */
      var error = card.querySelector('[data-sv2-bulb-error]');
      if (error) error.hidden = true;
    },
    true
  );

  /* ---- 2. Restore checked state after variant morphs -------------- */
  document.addEventListener('variant:update', function (event) {
    var detail = event && event.detail;
    /* Combined-listing switch loads a different product; server default
       (unchecked) is the correct state for it. */
    if (detail && detail.data && detail.data.newProduct) return;
    requestAnimationFrame(function () {
      var cards = allCards();
      for (var i = 0; i < cards.length; i++) {
        var input = cardInput(cards[i]);
        if (!input) continue;
        var want = checkedByProduct[cards[i].getAttribute('data-sv2-product-id')];
        if (typeof want === 'boolean' && input.checked !== want) {
          input.checked = want;
        }
      }
    });
  });

  /* ---- 3. bfcache restore: DOM is the source of truth ------------- */
  window.addEventListener('pageshow', function () {
    var cards = allCards();
    for (var i = 0; i < cards.length; i++) {
      var input = cardInput(cards[i]);
      if (!input) continue;
      checkedByProduct[cards[i].getAttribute('data-sv2-product-id')] = input.checked;
    }
  });

  /* ---- 4. Ride along on the theme's successful add ----------------- */
  document.addEventListener('cart:update', function (event) {
    var detail = event && event.detail;
    var data = detail && detail.data;
    if (!data) return;
    /* Only the theme's own product-form adds; our follow-up dispatch uses
       source 'sv2-bulb-addon', so no loop is possible. */
    if (data.source !== 'product-form-component') return;
    if (data.didError) return;
    if (!data.productId) return;

    var card = document.querySelector(
      '[data-sv2-bulb][data-sv2-product-id="' + String(data.productId) + '"]'
    );
    if (!card) return;

    /* Re-read state from the live DOM at add time — never cached. */
    var input = cardInput(card);
    if (!input || !input.checked) return;

    var variantId = parseInt(card.getAttribute('data-sv2-bulb-variant'), 10);
    if (!variantId) return;

    /* One bulb per lamp unit added in this action. */
    var quantity = parseInt(data.itemCount, 10);
    if (!quantity || quantity < 1) quantity = 1;
    if (quantity > 99) quantity = 99;

    addBulb(card, variantId, quantity);
  });

  function cartSectionIds() {
    var ids = [];
    var components = document.querySelectorAll('cart-items-component');
    for (var i = 0; i < components.length; i++) {
      var id = components[i].getAttribute('data-section-id');
      if (id) ids.push(id);
    }
    return ids.join(',');
  }

  function cartAddUrl() {
    if (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) {
      return window.Theme.routes.cart_add_url;
    }
    return '/cart/add.js';
  }

  function addBulb(card, variantId, quantity) {
    var payload = { items: [{ id: variantId, quantity: quantity }] };
    var sections = cartSectionIds();
    if (sections) payload.sections = sections;

    fetch(cartAddUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        if (response.status) {
          /* Shopify error shape: { status, message, description } */
          showError(card);
          return;
        }
        /* Replicate the theme: fetch the full cart, then dispatch the same
           event shape so drawer/items/counters update identically. */
        return fetch('/cart.js')
          .then(function (r) {
            return r.json();
          })
          .catch(function () {
            /* cart-items-component self-heals by re-fetching its section
               when resource/sections are missing. */
            return undefined;
          })
          .then(function (cart) {
            document.dispatchEvent(
              new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  resource: cart,
                  sourceId: 'sv2-bulb-addon',
                  data: {
                    source: 'sv2-bulb-addon',
                    itemCount: quantity,
                    productId: card.getAttribute('data-sv2-bulb-product-id') || '',
                    sections: response.sections,
                  },
                },
              })
            );
            announce(card, 'Bulb added to cart.');
          });
      })
      .catch(function (error) {
        console.warn('[sv2-bulb] add failed:', error);
        showError(card);
      });
  }

  function showError(card) {
    var error = card.querySelector('[data-sv2-bulb-error]');
    if (error) error.hidden = false;
    announce(card, 'The bulb could not be added to the cart.');
  }

  function announce(card, text) {
    var live = card.querySelector('[data-sv2-bulb-live]');
    if (!live) return;
    live.textContent = text;
    setTimeout(function () {
      if (live.textContent === text) live.textContent = '';
    }, LIVE_REGION_CLEAR_MS);
  }
})();

/* ===== guide.js ===== */
/* ============================================================
   Sollie PDP v2 — Measuring guide tabs (partial: guide.js)

   Morph-safe by construction:
   - Zero per-element listeners, zero runtime-injected DOM inside
     the product section. All events are delegated on document.
   - Tab state lives in server-rendered attributes/classes. When a
     Horizon variant change morphs the section, the markup resets
     to its server default (first tab active) — always consistent.
   - The only runtime flag is the .sv2-guide-js class on <html>
     (outside any morphed section); it flips CSS from the no-JS
     stacked-panels fallback to tab mode. Re-asserted on
     'pageshow' so bfcache restores self-heal.
   - Details-safe: the guide now lives inside a collapsed
     <details class="sv2-acc"> in the buy column. Delegated
     listeners fire regardless of open state, activate() only
     toggles classes/attributes (no measuring, so nothing breaks
     while hidden), and tab <button type="button"> clicks never
     toggle the <details> — only its <summary> does. No-JS keeps
     the native summary toggle + stacked panels.

   Load with `defer`. Dependency-free. Idempotent if included twice.
   ============================================================ */
(function () {
  'use strict';

  if (window.__sv2GuideInit) return;
  window.__sv2GuideInit = true;

  var JS_CLASS = 'sv2-guide-js';

  function flagJs() {
    var root = document.documentElement;
    if (root && !root.classList.contains(JS_CLASS)) root.classList.add(JS_CLASS);
  }

  flagJs();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', flagJs);
  }
  window.addEventListener('pageshow', flagJs);

  function closestTab(node) {
    if (!node || node.nodeType !== 1 || typeof node.closest !== 'function') return null;
    return node.closest('.sv2-guide__tab');
  }

  function guideTabs(guide) {
    return Array.prototype.slice.call(guide.querySelectorAll('.sv2-guide__tab'));
  }

  function activate(tab, moveFocus) {
    var guide = tab.closest('.sv2-guide');
    if (!guide) return;
    guideTabs(guide).forEach(function (t) {
      var selected = t === tab;
      t.setAttribute('aria-selected', selected ? 'true' : 'false');
      t.tabIndex = selected ? 0 : -1;
      var panel = document.getElementById(t.getAttribute('aria-controls') || '');
      if (panel) panel.classList.toggle('sv2-guide__panel--active', selected);
    });
    if (moveFocus) tab.focus();
  }

  document.addEventListener('click', function (event) {
    var tab = closestTab(event.target);
    if (tab) activate(tab, false);
  });

  document.addEventListener('keydown', function (event) {
    var tab = closestTab(event.target);
    if (!tab) return;
    var guide = tab.closest('.sv2-guide');
    if (!guide) return;

    var tabs = guideTabs(guide);
    var index = tabs.indexOf(tab);
    if (index === -1) return;

    var next = null;
    if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
    else if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
    else if (event.key === 'Home') next = tabs[0];
    else if (event.key === 'End') next = tabs[tabs.length - 1];

    if (next) {
      event.preventDefault();
      activate(next, true);
    }
  });
})();

/* ===== fitcalc.js ===== */
/* ============================================================
   Sollie PDP v2 — "Will it fit my room?" calculator (partial: fitcalc.js)

   Morph-safe by construction (Horizon morphs the product section on variant
   change and strips injected DOM):
   - Zero cached element references. Every handler re-reads the DOM from the
     event target's closest .sv2-fit at call time.
   - All listeners are delegated on `document` — they keep working after a
     morph replaces the section markup.
   - No persistent JS state that the DOM doesn't already carry: the active
     unit lives on the section's data-sv2-active-unit attribute, room type in
     the <select>, inputs in their fields. A morph resets these to the
     server defaults (cm, default room, empty) and clears results — always a
     consistent state; the buyer just re-submits.
   - Re-flags the JS class on 'pageshow' so bfcache restores self-heal.

   Formulas (research/measuring-tips.md, industry standard):
   - Ideal diameter (in) = room length(ft) + room width(ft); cm = in x 2.54.
   - Variant fit window = recommended diameter +/- 15%.
   - Dining hang: 30-36 in (76-91 cm) above table at 8 ft ceiling,
     +3 in per extra foot of ceiling.
   - Kitchen island: 30-36 in above counter; space pendants 24-30 in apart.
   - Living/Entry fixture height: 2-3 in per foot of ceiling; >=7 ft
     (213 cm) floor clearance in walk-under areas. Entry at 8 ft => 16-24 in.
   - Bedroom (bedside pendant): 30-36 in (76-91 cm) above the mattress.

   Load with `defer`. Dependency-free. Idempotent if included twice.
   ============================================================ */
(function () {
  'use strict';

  if (window.__sv2FitInit) return;
  window.__sv2FitInit = true;

  var JS_CLASS = 'sv2-fit-js';
  var IN_PER_CM = 1 / 2.54;
  var CM_PER_FT = 30.48;
  var IN_PER_FT = 12;

  function flagJs() {
    var root = document.documentElement;
    if (root && !root.classList.contains(JS_CLASS)) root.classList.add(JS_CLASS);
  }
  flagJs();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', flagJs);
  }
  window.addEventListener('pageshow', flagJs);

  /* ---------- helpers (all pure or DOM-reading, none cache) ---------- */

  function closestFit(node) {
    if (!node || node.nodeType !== 1 || typeof node.closest !== 'function') return null;
    return node.closest('.sv2-fit');
  }

  function round(n) { return Math.round(n); }

  function toNumber(el) {
    if (!el) return NaN;
    var v = parseFloat(String(el.value).replace(',', '.'));
    return isFinite(v) ? v : NaN;
  }

  function activeUnit(fit) {
    return fit.getAttribute('data-sv2-active-unit') === 'in' ? 'in' : 'cm';
  }

  // value (in the given unit) -> feet
  function toFeet(value, unit) {
    return unit === 'in' ? value / IN_PER_FT : value / CM_PER_FT;
  }

  // inches -> "NN cm / NN in"
  function bothFromIn(inches) {
    return round(inches * 2.54) + ' cm / ' + round(inches) + ' in';
  }
  // cm -> "NN cm / NN in"
  function bothFromCm(cm) {
    return round(cm) + ' cm / ' + round(cm * IN_PER_CM) + ' in';
  }
  // inclusive range from inches
  function rangeFromIn(loIn, hiIn) {
    return round(loIn * 2.54) + '–' + round(hiIn * 2.54) + ' cm / ' +
           round(loIn) + '–' + round(hiIn) + ' in';
  }

  function readVariants(fit) {
    var raw = fit.getAttribute('data-sv2-variants');
    if (!raw) return [];
    try {
      var arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }

  function esc(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ---------- hang-height per room type ---------- */
  // returns { key, value, note } where key/value are display strings.
  function hangHeight(room, ceilingFt) {
    var hasCeiling = isFinite(ceilingFt) && ceilingFt > 0;

    if (room === 'dining') {
      var extra = hasCeiling && ceilingFt > 8 ? (ceilingFt - 8) * 3 : 0;
      return {
        key: 'Hang above the table',
        value: rangeFromIn(30 + extra, 36 + extra),
        note: 'Measured from the tabletop to the bottom of the fixture' +
          (extra > 0 ? ' (raised for your taller ceiling: +3 in per extra foot).' : ' at a standard 8 ft (2.4 m) ceiling.') +
          ' Keep the fixture at least 12 in (30 cm) narrower than your table.'
      };
    }
    if (room === 'kitchen') {
      return {
        key: 'Hang above the counter',
        value: rangeFromIn(30, 36),
        note: 'Measured from the countertop to the bottom of the pendant. Space multiple pendants 24–30 in (61–76 cm) apart and keep 6 in (15 cm) in from each end.'
      };
    }
    if (room === 'bedroom') {
      return {
        key: 'Hang above the mattress',
        value: rangeFromIn(30, 36),
        note: 'For a bedside pendant, measure from the mattress top to the bottom of the fixture — about 20 in (51 cm) above the nightstand.'
      };
    }
    // living + entry: fixture body height 2-3 in / foot of ceiling
    if (hasCeiling) {
      return {
        key: 'Ideal fixture height',
        value: rangeFromIn(ceilingFt * 2, ceilingFt * 3),
        note: 'About 2–3 in of fixture height per foot of ceiling. Keep at least 7 ft (213 cm) of clearance from the floor anywhere people walk under it.'
      };
    }
    return {
      key: 'Floor clearance',
      value: '≥ 213 cm / ≥ 7 ft',
      note: 'Keep at least 7 ft (213 cm) between the floor and the bottom of the fixture in walk-under areas. Add a ceiling height above to size the fixture’s own height (2–3 in per foot of ceiling).'
    };
  }

  /* ---------- render ---------- */

  function showError(fit, msg) {
    var err = fit.querySelector('[data-sv2-fit-err]');
    var results = fit.querySelector('[data-sv2-results]');
    if (results) { results.hidden = true; results.innerHTML = ''; }
    if (err) { err.textContent = msg; err.hidden = false; }
  }

  function clearError(fit) {
    var err = fit.querySelector('[data-sv2-fit-err]');
    if (err) { err.hidden = true; err.textContent = ''; }
  }

  function chipHtml(v, loCm, hiCm) {
    var cm = v.cm;
    var dim = round(cm) + ' cm / ' + round(cm * IN_PER_CM) + ' in';
    var fits = cm >= loCm && cm <= hiCm;
    var verdict, cls;
    if (fits) { verdict = '✓ Fits your room'; cls = 'sv2-fit__chip--fit'; }
    else if (cm < loCm) { verdict = 'Too small'; cls = 'sv2-fit__chip--off'; }
    else { verdict = 'Too large'; cls = 'sv2-fit__chip--off'; }
    var name = esc(v.title);
    if (v.available === false) name += ' (sold out)';
    return '<li class="sv2-fit__chip ' + cls + '">' +
      '<span class="sv2-fit__chip-name">' + name + '</span>' +
      '<span class="sv2-fit__chip-dim">Ø ' + dim + '</span>' +
      '<span class="sv2-fit__chip-verdict">' + verdict + '</span>' +
      '</li>';
  }

  function compute(fit) {
    var unit = activeUnit(fit);
    var lengthV = toNumber(fit.querySelector('[data-sv2-length]'));
    var widthV = toNumber(fit.querySelector('[data-sv2-width]'));
    var ceilingV = toNumber(fit.querySelector('[data-sv2-ceiling]'));
    var roomEl = fit.querySelector('[data-sv2-room]');
    var room = roomEl ? roomEl.value : 'living';

    if (!(lengthV > 0) || !(widthV > 0)) {
      showError(fit, 'Enter your room length and width to see your fit.');
      return;
    }
    clearError(fit);

    var lFt = toFeet(lengthV, unit);
    var wFt = toFeet(widthV, unit);
    var ceilingFt = ceilingV > 0 ? toFeet(ceilingV, unit) : NaN;

    // ideal diameter
    var diaIn = lFt + wFt;
    var diaCm = diaIn * 2.54;
    var loCm = diaCm * 0.85;
    var hiCm = diaCm * 1.15;

    var hang = hangHeight(room, ceilingFt);

    // variant chips (only those with a parseable cm; graceful fallback if none)
    var variants = readVariants(fit);
    var sized = [];
    for (var i = 0; i < variants.length; i++) {
      if (variants[i] && typeof variants[i].cm === 'number' && variants[i].cm > 0) {
        sized.push(variants[i]);
      }
    }

    var html = '' +
      '<div class="sv2-fit__reveal">' +
        '<p class="sv2-fit__metric-k">Ideal fixture diameter</p>' +
        '<p class="sv2-fit__big">' + round(diaCm) + ' cm ' +
          '<span class="sv2-fit__big-alt">/ ' + round(diaIn) + ' in</span></p>' +
        '<div class="sv2-fit__accent-line" aria-hidden="true"></div>' +
        '<p class="sv2-fit__note">Sweet spot is your room’s length + width in feet, in inches. A comfortable range for this room is ' +
          round(loCm) + '–' + round(hiCm) + ' cm (' + round(loCm * IN_PER_CM) + '–' + round(hiCm * IN_PER_CM) + ' in).</p>' +

        '<div class="sv2-fit__block">' +
          '<p class="sv2-fit__block-k">' + esc(hang.key) + '</p>' +
          '<p class="sv2-fit__block-v">' + hang.value + '</p>' +
          '<p class="sv2-fit__note">' + hang.note + '</p>' +
        '</div>';

    if (sized.length) {
      html += '<div class="sv2-fit__block">' +
        '<p class="sv2-fit__block-k">This light’s sizes for your room</p>' +
        '<ul class="sv2-fit__chips">';
      for (var j = 0; j < sized.length; j++) {
        html += chipHtml(sized[j], loCm, hiCm);
      }
      html += '</ul></div>';
    }

    html += '</div>';

    var results = fit.querySelector('[data-sv2-results]');
    if (results) {
      results.innerHTML = html;
      results.hidden = false;
    }
  }

  /* ---------- unit toggle ---------- */

  function setUnit(fit, unit) {
    fit.setAttribute('data-sv2-active-unit', unit);
    var buttons = fit.querySelectorAll('[data-sv2-unit]');
    for (var i = 0; i < buttons.length; i++) {
      var on = buttons[i].getAttribute('data-sv2-unit') === unit;
      buttons[i].classList.toggle('is-active', on);
      buttons[i].setAttribute('aria-pressed', on ? 'true' : 'false');
    }
    var labels = fit.querySelectorAll('[data-sv2-unit-label]');
    for (var k = 0; k < labels.length; k++) labels[k].textContent = unit;

    // if results are already visible, recompute in the new unit
    var results = fit.querySelector('[data-sv2-results]');
    if (results && !results.hidden) compute(fit);
  }

  /* ---------- delegated listeners ---------- */

  document.addEventListener('click', function (event) {
    var unitBtn = event.target && event.target.nodeType === 1
      ? event.target.closest('[data-sv2-unit]') : null;
    if (unitBtn) {
      var fit = closestFit(unitBtn);
      if (fit) setUnit(fit, unitBtn.getAttribute('data-sv2-unit') === 'in' ? 'in' : 'cm');
    }
  });

  document.addEventListener('submit', function (event) {
    var form = event.target && event.target.nodeType === 1
      ? event.target.closest('[data-sv2-fit-form]') : null;
    if (!form) return;
    event.preventDefault();
    var fit = closestFit(form);
    if (fit) compute(fit);
  });
})();

/* ===== activity.js ===== */
/* sollie-pdp-v2 — "live activity" recent-purchase toast (.sv2-act*)
 *
 * ETHICS / GATING — READ FIRST:
 *   DEMO mode shows sample names for owner preview only; NEVER enable demo/fake
 *   data on the live customer storefront (Google Merchant Center
 *   misrepresentation + FTC fake-social-proof risk).
 *
 * Two modes, decided at runtime:
 *   • DEMO — cycles a built-in list of varied sample names + cities so the
 *            owner can preview the look. Enabled ONLY when the URL contains
 *            `sv2_activity=demo`, OR the preview sets data-sv2-activity="demo"
 *            on <body>, OR the config node carries data-sv2-act-demo="true".
 *   • REAL — production default. Reads window.SollieActivity.fetchReal(), which
 *            is stubbed to return [] until a genuine recent-orders feed exists.
 *            When real data is empty, the popup shows NOTHING.
 *
 * Dependency-free IIFE. The toast container is built at <body> level so Horizon
 * section morphs never strip it; it self-heals on pageshow. Listeners are on
 * document/window only; per-toast handlers live on the card element itself.
 */
(function () {
  'use strict';

  if (window.__sv2ActivityInit) return;
  window.__sv2ActivityInit = true;

  var CONTAINER_ID = 'sv2-act-root';
  var SHOW_MS = 5000; /* auto-dismiss after ~5s */
  var GAP_MIN_MS = 8000; /* next toast after a random 8–20s gap */
  var GAP_MAX_MS = 20000;
  var IN_DELAY_MS = 40; /* let layout settle before adding the --in class */
  var STICKY_SEL = '.sticky-add-to-cart, .msticky, [data-sticky-add-to-cart]';

  /* ---- Public hook (REAL / production mode) ------------------------------
   * window.SollieActivity.fetchReal() must resolve to an array of GENUINE
   * recent orders, each { name, city, minutesAgo, image? }. It is stubbed to
   * [] on purpose so the live storefront shows nothing until real data exists. */
  window.SollieActivity = window.SollieActivity || {};
  if (typeof window.SollieActivity.fetchReal !== 'function') {
    window.SollieActivity.fetchReal = function () {
      // wire to a real recent-orders feed or a recent-sales app; returns [] until real data exists
      // e.g. return fetch('/apps/recent-sales/recent.json')
      //   .then(function (r) { return r.json(); })
      //   .then(function (d) { return (d && d.items) || []; });
      return Promise.resolve([]);
    };
  }

  /* ---- Demo sample set (owner preview only) ------------------------------
   * Diverse international first names across Sollie's AU / US / CA / UK
   * markets, so the owner can see how varied names render. Never shipped to
   * customers — gated behind the demo flag. */
  var DEMO_SAMPLES = [
    { name: 'Maya', city: 'Sydney' },
    { name: 'Liam', city: 'Toronto' },
    { name: 'Noah', city: 'Manchester' },
    { name: 'Amara', city: 'Melbourne' },
    { name: 'Sofia', city: 'Austin' },
    { name: 'Kai', city: 'Brisbane' },
    { name: 'Olivia', city: 'Vancouver' },
    { name: 'Arjun', city: 'London' },
    { name: 'Emma', city: 'Perth' },
    { name: 'Mateo', city: 'Calgary' },
    { name: 'Chloe', city: 'Leeds' },
    { name: 'Priya', city: 'Adelaide' },
    { name: 'Lucas', city: 'Denver' },
    { name: 'Aisha', city: 'Bristol' },
    { name: 'Ethan', city: 'Seattle' },
    { name: 'Freya', city: 'Edinburgh' },
    { name: 'Diego', city: 'Montreal' },
    { name: 'Hannah', city: 'Portland' },
    { name: 'Omar', city: 'Glasgow' },
    { name: 'Grace', city: 'Ottawa' },
    { name: 'Nikhil', city: 'Chicago' },
    { name: 'Isla', city: 'Gold Coast' }
  ];

  var currentCard = null;
  var showTimer = null;
  var gapTimer = null;
  var stopped = false;
  var offsetScheduled = false;

  function randInt(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  function config() {
    var node = document.querySelector('[data-sv2-act-config]');
    if (!node) return null;
    return {
      image: node.getAttribute('data-sv2-act-image') || '',
      alt: node.getAttribute('data-sv2-act-alt') || '',
      demoAttr: node.getAttribute('data-sv2-act-demo') === 'true'
    };
  }

  function isDemo(cfg) {
    try {
      if (
        window.location &&
        typeof window.location.search === 'string' &&
        window.location.search.indexOf('sv2_activity=demo') !== -1
      ) {
        return true;
      }
    } catch (e) {
      /* ignore */
    }
    if (document.body && document.body.getAttribute('data-sv2-activity') === 'demo') return true;
    if (cfg && cfg.demoAttr) return true;
    return false;
  }

  /* ---- Container (body-level, morph-safe, self-healing) ------------------ */
  function ensureContainer() {
    var el = document.getElementById(CONTAINER_ID);
    if (!el) {
      el = document.createElement('div');
      el.id = CONTAINER_ID;
      el.className = 'sv2-act';
      el.setAttribute('aria-live', 'polite');
      el.setAttribute('role', 'status');
      document.body.appendChild(el);
    } else if (el.parentNode !== document.body) {
      document.body.appendChild(el);
    }
    return el;
  }

  /* ---- Keep clear of the sticky Add-to-cart bar ------------------------- */
  function stickyBottomHeight() {
    var bars = document.querySelectorAll(STICKY_SEL);
    var lift = 0;
    for (var i = 0; i < bars.length; i++) {
      var b = bars[i];
      var cs = window.getComputedStyle(b);
      if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) continue;
      var r = b.getBoundingClientRect();
      if (r.height === 0) continue;
      /* Only count a bar that is currently anchored to the bottom of the viewport. */
      if (r.bottom >= window.innerHeight - 8 && r.top < window.innerHeight) {
        lift = Math.max(lift, window.innerHeight - r.top);
      }
    }
    return lift;
  }

  function updateOffset() {
    var root = document.getElementById(CONTAINER_ID);
    if (!root) return;
    var lift = stickyBottomHeight();
    root.style.bottom = lift > 0 ? lift + 12 + 'px' : '';
  }

  /* ---- Card ------------------------------------------------------------- */
  function buildCard(item, cfg) {
    var card = document.createElement('div');
    card.className = 'sv2-act__card';
    card.setAttribute('role', 'group');

    var imgSrc = item.image || (cfg && cfg.image) || '';
    if (imgSrc) {
      var thumb = document.createElement('span');
      thumb.className = 'sv2-act__thumb';
      var img = document.createElement('img');
      img.className = 'sv2-act__img';
      img.src = imgSrc;
      img.alt = ''; /* decorative — the product context is carried in the text */
      img.width = 44;
      img.height = 44;
      img.loading = 'lazy';
      thumb.appendChild(img);
      card.appendChild(thumb);
    }

    var body = document.createElement('div');
    body.className = 'sv2-act__body';

    var name = document.createElement('p');
    name.className = 'sv2-act__name';
    name.textContent = item.name + ' in ' + item.city;
    body.appendChild(name);

    var mins = item.minutesAgo != null ? item.minutesAgo : 0;
    var meta = document.createElement('p');
    meta.className = 'sv2-act__meta';
    meta.textContent = 'purchased this · ' + mins + ' min ago';
    body.appendChild(meta);

    card.appendChild(body);

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'sv2-act__close';
    close.setAttribute('aria-label', 'Dismiss');
    close.textContent = '×';
    close.addEventListener('click', function () {
      dismiss();
      stop(); /* a manual dismiss ends the cycle for this pageview */
    });
    card.appendChild(close);

    return card;
  }

  function showItem(item, cfg) {
    var root = ensureContainer();
    if (currentCard && currentCard.parentNode) {
      currentCard.parentNode.removeChild(currentCard);
    }
    var card = buildCard(item, cfg);
    root.appendChild(card);
    currentCard = card;
    updateOffset();

    requestAnimationFrame(function () {
      setTimeout(function () {
        card.classList.add('sv2-act--in');
      }, IN_DELAY_MS);
    });

    clearTimeout(showTimer);
    showTimer = setTimeout(function () {
      dismiss();
    }, SHOW_MS);
  }

  function dismiss() {
    clearTimeout(showTimer);
    var card = currentCard;
    if (!card) return;
    card.classList.remove('sv2-act--in');
    var removed = false;
    var done = function () {
      if (removed) return;
      removed = true;
      if (card.parentNode) card.parentNode.removeChild(card);
      if (currentCard === card) currentCard = null;
    };
    card.addEventListener('transitionend', done);
    setTimeout(done, 450); /* fallback when the transition is suppressed (reduced motion) */
  }

  /* ---- Cycles ----------------------------------------------------------- */
  function nextGap() {
    return SHOW_MS + randInt(GAP_MIN_MS, GAP_MAX_MS);
  }

  function nextDemoItem(cfg) {
    var s = DEMO_SAMPLES[randInt(0, DEMO_SAMPLES.length - 1)];
    return { name: s.name, city: s.city, minutesAgo: randInt(2, 47), image: cfg ? cfg.image : '' };
  }

  function runDemo(cfg) {
    if (stopped) return;
    showItem(nextDemoItem(cfg), cfg);
    clearTimeout(gapTimer);
    gapTimer = setTimeout(function () {
      runDemo(cfg);
    }, nextGap());
  }

  function runReal(cfg) {
    Promise.resolve(window.SollieActivity.fetchReal())
      .then(function (items) {
        if (stopped || !items || !items.length) return; /* empty → show NOTHING */
        var i = 0;
        function step() {
          if (stopped || i >= items.length) return;
          var it = items[i++];
          showItem(
            {
              name: it.name,
              city: it.city,
              minutesAgo: it.minutesAgo != null ? it.minutesAgo : 0,
              image: it.image || (cfg ? cfg.image : '')
            },
            cfg
          );
          clearTimeout(gapTimer);
          gapTimer = setTimeout(step, nextGap());
        }
        step();
      })
      .catch(function () {
        /* Silent by design — never fabricate activity on a data failure. */
      });
  }

  /* ---- Lifecycle -------------------------------------------------------- */
  function stop() {
    stopped = true;
    clearTimeout(showTimer);
    clearTimeout(gapTimer);
  }

  function start() {
    if (!document.querySelector('[data-sv2-act-config]')) return; /* only on a PDP v2 render */
    stopped = false;
    var cfg = config();
    if (isDemo(cfg)) runDemo(cfg);
    else runReal(cfg);
  }

  function boot() {
    stop();
    stopped = false;
    if (currentCard && currentCard.parentNode) currentCard.parentNode.removeChild(currentCard);
    currentCard = null;
    start();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* bfcache restore re-runs the page from a frozen state — restart the cycle.
     On a normal first load DOMContentLoaded already booted, so only re-heal the
     container here (Horizon body-level nodes survive section morphs, but this
     guarantees it). */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) boot();
    else ensureContainer();
  });

  window.addEventListener(
    'scroll',
    function () {
      if (!currentCard || offsetScheduled) return;
      offsetScheduled = true;
      requestAnimationFrame(function () {
        offsetScheduled = false;
        updateOffset();
      });
    },
    { passive: true }
  );
  window.addEventListener(
    'resize',
    function () {
      if (currentCard) updateOffset();
    },
    { passive: true }
  );
})();

/* ===== recent.js ===== */
/* ============================================================
   Sollie PDP v2 — "Recently viewed" (partial: recent.js)

   Client-side product history in localStorage. On each PDP load:
     1. Read the current product from the section's
        data-sv2-rv-current JSON attribute (server-rendered, so the
        `money` filter already produced a correct-currency price).
     2. Prepend it to the 'sollie_recently_viewed' list — most-recent
        first, deduped by handle, capped at 8.
     3. Render up to 6 OTHER products (exclude the current handle) by
        cloning the section's <template>, then unhide the <section>
        only when >= 2 cards were rendered (else it stays hidden — no
        empty state).

   Morph-safe by construction:
     - Idempotent: render() clears + rebuilds the list container each
       run, so double-invocation can't duplicate cards.
     - No per-card listeners; cards are plain <a> links.
     - Re-runs on 'pageshow' (bfcache restore) and on Horizon's
       section morph — we re-scan the DOM for the section each time
       rather than caching a node reference.
     - Guarded against unavailable / disabled localStorage.

   Load with `defer`. Dependency-free IIFE.
   ============================================================ */
(function () {
  'use strict';

  var KEY = 'sollie_recently_viewed';
  var STORE_CAP = 8; // how many we remember
  var SHOW_CAP = 6; // how many OTHER products we render
  var MIN_SHOW = 2; // hide the whole section below this

  function readStore() {
    try {
      var raw = window.localStorage.getItem(KEY);
      if (!raw) return [];
      var arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }

  function writeStore(arr) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(arr));
    } catch (e) {
      /* private mode / quota — degrade silently */
    }
  }

  function parseCurrent(section) {
    var raw = section.getAttribute('data-sv2-rv-current');
    if (!raw) return null;
    try {
      var obj = JSON.parse(raw);
      if (obj && typeof obj.handle === 'string' && obj.handle) return obj;
    } catch (e) {}
    return null;
  }

  // Prepend current, dedupe by handle (keeping the freshest), cap length.
  function record(current) {
    var list = readStore();
    var next = [current];
    for (var i = 0; i < list.length; i++) {
      var item = list[i];
      if (item && item.handle && item.handle !== current.handle) {
        next.push(item);
      }
      if (next.length >= STORE_CAP) break;
    }
    writeStore(next);
    return next;
  }

  function fillCard(node, item) {
    var link = node.querySelector('[data-sv2-rv-link]');
    var img = node.querySelector('[data-sv2-rv-img]');
    var title = node.querySelector('[data-sv2-rv-title]');
    var price = node.querySelector('[data-sv2-rv-price]');

    node.querySelectorAll('[data-sv2-rv-link]').forEach(function(a){ a.setAttribute('href', item.url || '#'); });
    var choices = node.querySelector('sollie-card-options');
    if (choices) { choices.dataset.productUrl = item.url || ''; choices.dataset.title = item.title || ''; choices.dataset.primary = item.img || ''; }
    if (title) title.textContent = item.title || '';
    if (price) price.textContent = item.price || '';
    if (img) {
      if (item.img) {
        img.setAttribute('src', item.img);
        img.setAttribute('alt', item.title || '');
      } else {
        // no image — drop the tile <img> so the stage shows plain bg
        img.parentNode && img.parentNode.removeChild(img);
      }
    }
  }

  function render(section, list, currentHandle) {
    var listEl = section.querySelector('[data-sv2-rv-list]');
    var tpl = section.querySelector('.sv2-rv__tpl');
    if (!listEl || !tpl || !('content' in tpl)) return;

    // Idempotent: rebuild from scratch each run.
    listEl.textContent = '';

    var shown = 0;
    for (var i = 0; i < list.length && shown < SHOW_CAP; i++) {
      var item = list[i];
      if (!item || !item.handle || item.handle === currentHandle) continue;
      if (!item.url || !item.title) continue;

      var frag = tpl.content.cloneNode(true);
      var card = frag.querySelector('.sv2-rv__item');
      if (!card) continue;
      fillCard(frag, item);
      listEl.appendChild(frag);
      shown++;
    }

    if (shown >= MIN_SHOW) {
      section.removeAttribute('hidden');
    } else {
      section.setAttribute('hidden', '');
    }
  }

  function run() {
    var sections = document.querySelectorAll('[data-sv2-rv]');
    for (var i = 0; i < sections.length; i++) {
      var section = sections[i];
      var current = parseCurrent(section);
      var currentHandle = current ? current.handle : '';
      var list = current ? record(current) : readStore();
      render(section, list, currentHandle);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  // bfcache restore + Horizon section morph both fire pageshow.
  window.addEventListener('pageshow', run);
})();
